import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, FileStack, Copy, AlertTriangle, CheckCircle2, Layers, Split } from 'lucide-react';
import { toast } from 'sonner';
import { api, fmtDate, fmtMoney } from '../lib/api';

/**
 * BillPreviewDialog (Phase 11)
 * Shows candidate bills detected from a multi-page PDF upload and lets the
 * user commit as split (N bills) or combined (1 bill).
 *
 * Props:
 *   open              — controlled visibility
 *   onOpenChange(v)   — visibility setter
 *   previewId         — server-side preview identifier
 *   initialPreview    — first payload from /api/bills/upload (has status)
 *   onCommitted(res)  — called after a successful commit; res = commit response
 *   onDiscarded()     — called after Cancel / discard
 */
export function BillPreviewDialog({
    open,
    onOpenChange,
    previewId,
    initialPreview,
    onCommitted,
    onDiscarded,
}) {
    const [preview, setPreview] = useState(initialPreview || null);
    const [committing, setCommitting] = useState(null); // 'split' | 'combined' | null
    const pollRef = useRef(null);

    // Refresh state when the source props change
    useEffect(() => {
        setPreview(initialPreview || null);
    }, [initialPreview, previewId]);

    // Poll while the preview is still processing
    useEffect(() => {
        if (!open || !previewId) return undefined;
        if (preview?.status && preview.status !== 'processing') return undefined;

        let cancelled = false;
        const tick = async () => {
            try {
                const data = await api.getBillPreview(previewId);
                if (cancelled) return;
                setPreview(data);
                if (data.status === 'processing') {
                    pollRef.current = setTimeout(tick, 3000);
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(
                        e?.response?.data?.detail || 'Failed to fetch preview status',
                    );
                }
            }
        };
        pollRef.current = setTimeout(tick, 2000);
        return () => {
            cancelled = true;
            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [open, previewId, preview?.status]);

    const handleCancel = useCallback(async () => {
        if (previewId) {
            try {
                await api.discardBillPreview(previewId);
            } catch (e) {
                // ignore
            }
        }
        onOpenChange(false);
        onDiscarded?.();
    }, [previewId, onOpenChange, onDiscarded]);

    const handleCommit = useCallback(
        async (mode) => {
            if (!previewId) return;
            setCommitting(mode);
            try {
                const res = await api.commitBillPreview({
                    preview_id: previewId,
                    mode,
                    force: true, // user has already reviewed — allow duplicates through
                });
                const label =
                    mode === 'split'
                        ? `Saved ${res.created_count} bills`
                        : 'Saved combined bill';
                toast.success(label, {
                    description:
                        res.alerts_count > 0
                            ? `${res.alerts_count} price alert${res.alerts_count === 1 ? '' : 's'} detected`
                            : undefined,
                });
                onOpenChange(false);
                onCommitted?.(res);
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail || 'Failed to save bills',
                );
            } finally {
                setCommitting(null);
            }
        },
        [previewId, onCommitted, onOpenChange],
    );

    const status = preview?.status || 'processing';
    const pageCount = preview?.page_count || 0;
    const detected = preview?.detected_count || 0;
    const progress = preview?.progress || { total: pageCount, done: 0 };
    const candidates = preview?.candidate_bills || [];

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v) {
                    handleCancel();
                } else {
                    onOpenChange(v);
                }
            }}
        >
            <DialogContent
                data-testid="bill-preview-dialog"
                className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileStack size={18} className="text-primary" />
                        {status === 'ready'
                            ? `We detected ${detected} bill${detected === 1 ? '' : 's'} in this PDF`
                            : 'Scanning your PDF…'}
                    </DialogTitle>
                    <DialogDescription>
                        {status === 'ready'
                            ? 'Review the detected bills below. You can save them as separate bills (recommended) or merge them into a single bill.'
                            : `Processing ${pageCount} pages with AI. This can take up to ~2 minutes for large files.`}
                    </DialogDescription>
                </DialogHeader>

                {status === 'processing' && (
                    <div className="py-6 space-y-3" data-testid="preview-processing">
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <Loader2 size={16} className="animate-spin" />
                            Extracting page {progress.done || 0} of {progress.total || pageCount}…
                        </div>
                        <Progress
                            value={
                                progress.total
                                    ? Math.max(5, Math.round(((progress.done || 0) / progress.total) * 100))
                                    : 10
                            }
                            className="h-2"
                        />
                        <div className="text-xs text-muted-foreground">
                            You can leave this dialog open — we'll show the detected bills as soon as scanning finishes.
                        </div>
                    </div>
                )}

                {status === 'failed' && (
                    <Alert
                        variant="destructive"
                        className="my-3"
                        data-testid="preview-failed"
                    >
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Extraction failed</AlertTitle>
                        <AlertDescription>
                            {preview?.error || 'Something went wrong while scanning the PDF. Please try again.'}
                        </AlertDescription>
                    </Alert>
                )}

                {status === 'ready' && candidates.length === 0 && (
                    <Alert className="my-3" data-testid="preview-empty">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Nothing detected</AlertTitle>
                        <AlertDescription>
                            We couldn't find any bill data on these pages. Try uploading again or a clearer scan.
                        </AlertDescription>
                    </Alert>
                )}

                {status === 'ready' && candidates.length > 0 && (
                    <div
                        className="overflow-y-auto space-y-2 pr-1 -mr-1"
                        data-testid="preview-candidates"
                    >
                        {candidates.map((c) => (
                            <CandidateRow key={c.candidate_index} c={c} />
                        ))}
                    </div>
                )}

                <DialogFooter className="mt-4 gap-2 flex-col sm:flex-row">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={!!committing}
                        data-testid="preview-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <div className="flex flex-col sm:flex-row gap-2 sm:ml-auto">
                        <Button
                            variant="secondary"
                            disabled={status !== 'ready' || !!committing || candidates.length === 0}
                            onClick={() => handleCommit('combined')}
                            data-testid="preview-save-combined-btn"
                            className="gap-2"
                        >
                            {committing === 'combined' ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Layers size={16} />
                            )}
                            Save as 1 combined bill
                        </Button>
                        <Button
                            disabled={status !== 'ready' || !!committing || candidates.length === 0}
                            onClick={() => handleCommit('split')}
                            data-testid="preview-save-split-btn"
                            className="gap-2"
                        >
                            {committing === 'split' ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Split size={16} />
                            )}
                            Save all {detected} as separate bills
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CandidateRow({ c }) {
    const dupCount = (c.duplicate_pages || []).length;
    return (
        <div
            className="flex items-start gap-3 rounded-md border border-border/70 bg-background/60 p-3"
            data-testid={`preview-candidate-${c.candidate_index}`}
        >
            {c.thumbnail ? (
                <img
                    src={c.thumbnail}
                    alt=""
                    className="h-20 w-16 object-cover rounded-sm border border-border/60 shrink-0"
                />
            ) : (
                <div className="h-20 w-16 rounded-sm bg-muted/60 shrink-0 flex items-center justify-center text-muted-foreground">
                    <FileStack size={20} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="font-medium truncate" data-testid={`preview-candidate-${c.candidate_index}-vendor`}>
                            {c.vendor || 'Unknown vendor'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                            {c.invoice_number && <span>Invoice #{c.invoice_number}</span>}
                            {c.bill_date && <span>· {fmtDate(c.bill_date)}</span>}
                            <span>· {c.item_count} item{c.item_count === 1 ? '' : 's'}</span>
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="font-mono tabular-nums text-sm" data-testid={`preview-candidate-${c.candidate_index}-total`}>
                            {fmtMoney(c.total, c.currency)}
                        </div>
                    </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px]">
                        {c.pages.length > 1
                            ? `Pages ${c.pages[0]}–${c.pages[c.pages.length - 1]}`
                            : `Page ${c.pages[0]}`}
                    </Badge>
                    {dupCount > 0 && (
                        <Badge
                            variant="outline"
                            className="text-[10px] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]"
                        >
                            <Copy size={10} className="mr-1" />
                            {dupCount} duplicate copy page{dupCount === 1 ? '' : 's'}
                        </Badge>
                    )}
                    {c.gstin && (
                        <Badge variant="outline" className="text-[10px] font-mono">
                            GSTIN {c.gstin}
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
}
